import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiDelete, ApiError } from '../lib/apiClient';
import { DataTable } from '../components/DataTable';

/**
 * C2b — care plan oversight and templates (docs/administration.md §24.5.5).
 *
 * ── This screen shows a plan's shape, never its content ───────────────────────
 *
 * A care plan is clinical content about an identifiable patient. The backend strips
 * every clinical field before sending — title, description, notes, referral
 * specialty, attachment filename — so there is nothing here to hide client-side and
 * nothing in devtools to find.
 *
 * The UI states that plainly rather than rendering blanks. An admin who sees an empty
 * column assumes the data is missing or broken and goes looking for it; an admin who
 * is told the content is withheld by design does not. So the redaction is presented as
 * a property of the screen, not as absent data.
 *
 * What it does show is the thing oversight actually needs: does this plan have
 * substance. An item whose instructions are `none` is the case worth catching, and
 * that is visible without reading a word of the patient's plan.
 */
type PlanStatus = 'draft' | 'active' | 'in_progress' | 'completed';
type LengthBand = 'none' | 'short' | 'medium' | 'long';
type Tab = 'plans' | 'templates';

interface RedactedItem {
  id: string;
  type: string;
  status: string;
  order: number;
  titleLength: LengthBand;
  descriptionLength: LengthBand;
  hasResultAttachment: boolean;
}

interface RedactedPlan {
  id: string;
  caseId: string;
  specialistName: string;
  status: PlanStatus;
  progressPercent: number;
  itemCount: number;
  typeCounts: Record<string, number>;
  notesLength: LengthBand;
  items: RedactedItem[];
  updatedAt?: string;
}

interface Template {
  id: string;
  name: string;
  description?: string;
  items: Array<{ type: string; title: string; description?: string; order: number }>;
  active: boolean;
}

const STATUSES: PlanStatus[] = ['draft', 'active', 'in_progress', 'completed'];

const TEMPLATE_ITEM_TYPES = [
  'lab_order',
  'image_order',
  'referral',
  'self_referral',
  'medication',
  'lifestyle',
  'custom',
] as const;

/**
 * Human wording for a length band.
 *
 * `none` reads as an explicit absence rather than a blank, because "this item has no
 * instructions" is a finding, not a rendering gap.
 */
export function bandLabel(band: LengthBand | undefined): string {
  switch (band) {
    case 'none':
      return 'none';
    case 'short':
      return 'brief';
    case 'medium':
      return 'moderate';
    case 'long':
      return 'detailed';
    default:
      return '—';
  }
}

/**
 * Items whose content is empty — the oversight signal this screen exists for.
 *
 * A `medication` item with no instructions is the concrete thing worth surfacing, and
 * it is detectable from the bands alone.
 */
export function itemsMissingContent(plan: Pick<RedactedPlan, 'items'>): number {
  return (plan.items ?? []).filter(
    (i) => i.descriptionLength === 'none' || i.titleLength === 'none',
  ).length;
}

/** `{ medication: 2, lab_order: 1 }` → `medication ×2 · lab_order ×1`. */
export function summariseTypes(typeCounts: Record<string, number> | undefined): string {
  const entries = Object.entries(typeCounts ?? {});
  if (entries.length === 0) return '—';
  return entries
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([type, n]) => `${type} ×${n}`)
    .join(' · ');
}

export function CarePlansPage() {
  const [tab, setTab] = useState<Tab>('plans');
  const [status, setStatus] = useState<PlanStatus | 'all'>('all');
  const [plans, setPlans] = useState<RedactedPlan[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newName, setNewName] = useState('');
  const [newItems, setNewItems] = useState<Array<{ type: string; title: string }>>([
    { type: 'lab_order', title: '' },
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'templates') {
        const data = await apiGet<{ items: Template[] }>('/admin/care-plans/templates');
        setTemplates(data.items ?? []);
      } else {
        const q = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`;
        const data = await apiGet<{ items: RedactedPlan[] }>(`/admin/care-plans${q}`);
        setPlans(data.items ?? []);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load care plans');
    } finally {
      setLoading(false);
    }
  }, [tab, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const thinPlans = useMemo(() => plans.filter((p) => itemsMissingContent(p) > 0).length, [plans]);

  const createTemplate = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiPost('/admin/care-plans/templates', {
        name: newName,
        items: newItems
          .filter((i) => i.title.trim())
          .map((i, index) => ({ type: i.type, title: i.title.trim(), order: index })),
      });
      setNewName('');
      setNewItems([{ type: 'lab_order', title: '' }]);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create the template');
    } finally {
      setBusy(false);
    }
  };

  const retire = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await apiDelete(`/admin/care-plans/templates/${encodeURIComponent(id)}`);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to retire the template');
    } finally {
      setBusy(false);
    }
  };

  const canCreate = newName.trim().length > 0 && newItems.some((i) => i.title.trim());

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Patient Care Plans</h1>
          <p className="cell-sub">
            {tab === 'plans' ? (
              <>
                The clinical plans specialists write <strong>for patients</strong> — lab orders,
                referrals, medications. Looking for the LynkPulse Free/Plus/Guardian subscription
                tiers instead? Those live under <strong>LynkPulse Plans &amp; Pricing</strong>.
                <br />
                Clinical content is <strong>withheld by design</strong> — titles, instructions,
                dosages and notes are stripped on the server and never sent here. What you see is
                each plan&apos;s shape: how many items, of what kind, and whether they have
                substance. Every view is recorded in the audit log.
              </>
            ) : (
              <>
                Approved starting points for specialists. Using one is{' '}
                <strong>optional</strong> — a specialist can still author a plan freehand.
                Templates contain no patient data.
              </>
            )}
          </p>
        </div>
        <div className="row-actions">
          <button
            type="button"
            className={tab === 'plans' ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
            aria-pressed={tab === 'plans'}
            onClick={() => setTab('plans')}
          >
            Plans
          </button>
          <button
            type="button"
            className={tab === 'templates' ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
            aria-pressed={tab === 'templates'}
            onClick={() => setTab('templates')}
          >
            Templates
          </button>
          <button type="button" className="btn btn-xs" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}

      {tab === 'plans' ? (
        <>
          <div className="row-actions" style={{ marginBottom: '0.75rem' }}>
            {(['all', ...STATUSES] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={s === status ? 'btn btn-xs btn-primary' : 'btn btn-xs'}
                aria-pressed={s === status}
                onClick={() => setStatus(s)}
              >
                {s}
              </button>
            ))}
          </div>

          {thinPlans > 0 && (
            <div className="warning-banner">
              {thinPlans} plan{thinPlans === 1 ? '' : 's'} contain items with no title or no
              instructions. That is visible without reading any clinical content, and is usually
              worth a word with the authoring specialist.
            </div>
          )}

          <DataTable
            controls={false}
            pageSize={null}
            rows={plans}
            loading={loading}
            empty="No care plans."
            getRowKey={(p: RedactedPlan) => p.id}
            noun="plan"
            columns={[
              { key: 'case', header: 'Case', value: (p: RedactedPlan) => p.caseId },
              { key: 'author', header: 'Authored by', value: (p: RedactedPlan) => p.specialistName },
              { key: 'status', header: 'Status', value: (p: RedactedPlan) => p.status },
              {
                key: 'items',
                header: 'Items',
                render: (p: RedactedPlan) => (
                  <div>
                    <div>{p.itemCount}</div>
                    <div className="cell-sub">{summariseTypes(p.typeCounts)}</div>
                  </div>
                ),
              },
              {
                key: 'substance',
                header: 'Content',
                render: (p: RedactedPlan) => {
                  const missing = itemsMissingContent(p);
                  return (
                    <div>
                      <div className="cell-sub">notes: {bandLabel(p.notesLength)}</div>
                      {missing > 0 && (
                        <div className="cell-sub">{missing} item(s) with nothing written</div>
                      )}
                    </div>
                  );
                },
              },
              {
                key: 'progress',
                header: 'Progress',
                value: (p: RedactedPlan) => `${p.progressPercent ?? 0}%`,
              },
            ]}
          />
        </>
      ) : (
        <>
          <div className="form-card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ marginBottom: '0.5rem' }}>New template</h2>
            <div className="row-actions" style={{ marginBottom: '0.5rem' }}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Template name, e.g. Type 2 diabetes intake"
                aria-label="Template name"
                style={{ minWidth: '18rem' }}
              />
            </div>

            {newItems.map((item, index) => (
              <div className="row-actions" key={index} style={{ marginBottom: '0.5rem' }}>
                <select
                  value={item.type}
                  aria-label={`Item ${index + 1} type`}
                  onChange={(e) =>
                    setNewItems((prev) =>
                      prev.map((it, i) => (i === index ? { ...it, type: e.target.value } : it)),
                    )
                  }
                >
                  {TEMPLATE_ITEM_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  value={item.title}
                  aria-label={`Item ${index + 1} title`}
                  placeholder="Item title, e.g. HbA1c"
                  onChange={(e) =>
                    setNewItems((prev) =>
                      prev.map((it, i) => (i === index ? { ...it, title: e.target.value } : it)),
                    )
                  }
                  style={{ minWidth: '14rem' }}
                />
                {newItems.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-xs"
                    onClick={() => setNewItems((prev) => prev.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <div className="row-actions">
              <button
                type="button"
                className="btn btn-xs"
                onClick={() => setNewItems((prev) => [...prev, { type: 'custom', title: '' }])}
              >
                Add item
              </button>
              <button
                type="button"
                className="btn btn-xs btn-primary"
                // The backend rejects a nameless or itemless template, so don't offer
                // a save that cannot succeed.
                disabled={!canCreate || busy}
                onClick={() => void createTemplate()}
              >
                Create template
              </button>
            </div>
          </div>

          <DataTable
            controls={false}
            pageSize={null}
            rows={templates}
            loading={loading}
            empty="No templates yet. Specialists are authoring plans freehand."
            getRowKey={(t: Template) => t.id}
            noun="template"
            columns={[
              { key: 'name', header: 'Name', value: (t: Template) => t.name },
              {
                key: 'items',
                header: 'Items',
                render: (t: Template) => (
                  <div>
                    <div>{t.items?.length ?? 0}</div>
                    <div className="cell-sub">
                      {(t.items ?? []).map((i) => i.type).join(' · ') || '—'}
                    </div>
                  </div>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (t: Template) => (
                  <button
                    type="button"
                    className="btn btn-xs"
                    disabled={busy}
                    // Retire, not delete: a plan built from this template keeps its
                    // provenance, and that content is already with patients.
                    onClick={() => void retire(t.id)}
                  >
                    Retire
                  </button>
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

export default CarePlansPage;
